import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { FormBuilder, Validators, FormGroup, AbstractControl } from '@angular/forms';
import { BasePaymentModalComponent } from '../base-payment-modal/base-payment-modal.component';

/**
 * @component CreditCardPaymentFormComponent
 * @description Component for handling credit card payment form within a modal.
 * Extends BasePaymentModalComponent to reuse common modal and form array logic.
 */
@Component({
	selector: 'app-credit-card-payment-form',
	templateUrl: './credit-card-payment-form.component.html',
	styleUrls: ['./credit-card-payment-form.component.scss']
})
export class CreditCardPaymentFormComponent extends BasePaymentModalComponent implements OnInit {
	/**
	 * @property {Array<{label: string, value: number}>} paymentTypeOptions - Options for payment types dropdown.
	 */
	paymentTypeOptions = [
		{ label: 'Normal', value: 1 },
		{ label: 'Payments', value: 2 },
		{ label: 'Credit', value: 3 },
		{ label: 'Deferred Payment', value: 4 },
		{ label: 'Others', value: 5 },
		{ label: 'Not Credit Card', value: 0 }
	];

	/**
	 * @property {Array<{label: string, value: number}>} creditTypeOptions - Options for credit card types dropdown.
	 */
	creditTypeOptions = [
		{ label: 'Isracard', value: 1 },
		{ label: 'Cal', value: 2 },
		{ label: 'Diners', value: 3 },
		{ label: 'American Express', value: 4 },
		{ label: 'Leumi Card', value: 6 },
		{ label: 'Not Credit Card', value: 0 }
	];

	/**
	 * @constructor
	 * @param {FormBuilder} fb - Angular FormBuilder service.
	 * @param {ChangeDetectorRef} cdr - Angular ChangeDetectorRef service.
	 */
	constructor(
		protected fb: FormBuilder,
		protected cdr: ChangeDetectorRef
	) {
		super(fb, cdr);
	}

	/**
	 * @method ngOnInit
	 * @description Initializes the component, checks for required input, updates totals, and sets up local form lines.
	 * @returns {void}
	 */
	ngOnInit(): void {
		if (!this.lines) {
			console.error("CreditCardPaymentFormComponent requires a lines FormArray input.");
			return;
		}
		this.initLocalLines();
	}

	/**
	 * @method addCreditCardLine
	 * @description Adds a new, empty credit card line to the local form array.
	 * @param {boolean} [updateTotal=true] - Whether to update the total display after adding the line.
	 * @returns {void}
	 */
	addCreditCardLine(updateTotal: boolean = true): void {
		this.addLineWithValidation(() => this.createEmptyLineGroup(), updateTotal);
	}

	/**
	 * @method onAmountInput
	 * @description Handles the amount input event for a specific line in the local form array.
	 * @param {{ originalEvent: Event, value: number | null }} event - The input event object.
	 * @param {number} index - The index of the line where the input occurred.
	 * @returns {void}
	 */
	onAmountInput(event: { originalEvent: Event, value: number | null }, index: number): void {
		this.onAmountInputLocal(event, index);
	}

	/**
	 * @method createEmptyLineGroup
	 * @description Creates a new FormGroup representing an empty credit card payment line with default values and validators.
	 * @returns {FormGroup} A new FormGroup for a credit card line.
	 */
	createEmptyLineGroup(): FormGroup {
		const today = new Date();
		today.setHours(0, 0, 0, 0);
		return this.fb.group({
			id: [null],
			payment_type: [3],
			date: [today, Validators.required],
			amount: [null, [Validators.required, Validators.min(0.01)]],
			credit_card_code: ['', Validators.required],
			credit_card_name: [null, Validators.required],
			credit_type: [null, Validators.required],
			account: ['', Validators.required]
		});
	}
}
